import { get, toUpper, trim } from 'lodash';
import { DEVICE_OWNER_WIRED, SPEED_MULTIPLIERS, DEVICE_OWNER_WIRELESS } from './DeviceConstants';

const NEW_STATE = 'new';
const DOWN_STATE = 'down';
const UNMONITORED_STATE = 'unmonitored';

export const isNewState = (state) => state === NEW_STATE;
export const isDownState = (state) => state === DOWN_STATE;
export const isUnmonitoredState = (state) => state === UNMONITORED_STATE;

/**
 * Check if device type is polled.
 * Access points are not polled, all other device types are.
 * @param {string} deviceType - Device type
 * @returns {boolean} True if device is polled
 */
export const isPolled = (deviceType) => deviceType !== 'accesspoint';

/**
 * Checks if a device can be rescanned.
 * Only wired, non-disabled devices are reclassifiable.
 * @param {Object} formData - Device form data
 * @returns {boolean} True if device can be rescanned
 */
export const canReclassify = (formData) => {
    const disabled = get(formData, 'disabled', false);
    const owner = get(formData, 'owner', '');
    return !disabled && owner === DEVICE_OWNER_WIRED;
};

/**
 * Checks if a device can be deleted.
 * Only wired-owned devices can be deleted (disabled status does not matter).
 * @param {Object} formData - Device form data
 * @returns {boolean} True if device can be deleted
 */
export const canDelete = (formData) => {
    const owner = get(formData, 'owner', '');
    return owner === DEVICE_OWNER_WIRED || owner === DEVICE_OWNER_WIRELESS;
};

/**
 * Checks if a device is currently disabled.
 * @param {Object} formData - Device form data
 * @returns {boolean} True if device is disabled
 */
export const isDeviceDisabled = (formData) => {
    return get(formData, 'disabled', false);
};

/**
 * Parses a speed string with optional K/M/G/T suffix into bps.
 * Examples: "40G" → 40000000000, "100M" → 100000000, "500000" → 500000
 * @param {string} speedStr - Speed string to parse
 * @returns {number|null} Speed in bps, or null if invalid
 */
export const parseSpeedString = (speedStr) => {
    if (!speedStr) return null;

    const cleaned = trim(speedStr);
    const match = cleaned.match(/^(\d+)\s*([KMGT])?$/i);
    if (!match) return null;

    const value = parseInt(match[1], 10);
    const suffix = match[2] ? toUpper(match[2]) : null;

    if (value <= 0) return null;

    const multiplier = suffix ? get(SPEED_MULTIPLIERS, suffix, 1) : 1;
    return value * multiplier;
};

/**
 * Validates a speed input string.
 * @param {string} speedStr - Speed string to validate
 * @returns {boolean} True if valid speed format
 */
export const isValidSpeedString = (speedStr) => {
    if (!speedStr) return false;
    const cleaned = trim(speedStr);
    return /^\d+\s*[KMGT]?$/i.test(cleaned) && parseSpeedString(cleaned) > 0;
};
